import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/core';
import { debounce } from 'lodash';
import {
  CheckboxField,
  SearchField,
  Text,
  useControlledField,
} from '@weave/design-system';
import { WeaveTheme } from '@weave/theme-original';

export type OfficeOption = {
  id: string;
  name: string;
  address: string;
};

type Props = {
  offices: OfficeOption[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  label?: string;
  className?: string;
};

/**
 * OfficeMultiSelect renders a list of offices as checkboxes with:
 * - a parent "Select All" checkbox that selects/deselects every office and
 *   displays an indeterminate state when only some offices are selected
 * - a search bar that filters the visible offices by name, id or address
 */
export const OfficeMultiSelect = ({
  offices,
  selectedIds,
  onChange,
  label = 'Select Offices (Multi-Select)',
  className,
}: Props) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );

  useEffect(() => () => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm]);

  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchInput,
    onChange: (value: string) => {
      setSearchInput(value);
      debouncedSetSearchTerm(value);
    },
  });

  const filteredOffices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return offices;
    }
    return offices.filter((office) => {
      return (
        office.name.toLowerCase().includes(term) ||
        office.id.toLowerCase().includes(term) ||
        office.address.toLowerCase().includes(term)
      );
    });
  }, [offices, searchTerm]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Count only ids that map to a known office, so stale selections don't skew
  // the parent checkbox state.
  const selectedCount = useMemo(
    () =>
      offices.reduce(
        (total, office) => (selectedSet.has(office.id) ? total + 1 : total),
        0
      ),
    [offices, selectedSet]
  );

  const allSelected = offices.length > 0 && selectedCount === offices.length;
  const someSelected = selectedCount > 0 && !allSelected;

  // The design system's checkbox only renders the indeterminate icon inside a
  // filled box when `value` is truthy, so we treat "partial" as visually
  // checked while still flagging it as indeterminate.
  const parentFieldProps = useControlledField({
    type: 'checkbox',
    value: allSelected || someSelected,
    onChange: () => {
      if (allSelected) {
        onChange([]);
      } else {
        onChange(offices.map((office) => office.id));
      }
    },
  });

  const toggleOffice = useCallback(
    (id: string) => {
      const next = new Set(selectedSet);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onChange(Array.from(next));
    },
    [onChange, selectedSet]
  );

  return (
    <div css={containerStyle} className={className}>
      <Text as="label" size="small" color="light" css={labelStyle}>
        {label}
      </Text>
      <div css={cardStyle}>
        <div css={headerRowStyle}>
          <CheckboxField
            {...parentFieldProps}
            name="office-multi-select__select-all"
            indeterminate={someSelected}
            label={`Select All (${offices.length} Offices)`}
          />
          <Text as="span" size="small" color="light">
            {selectedCount} selected
          </Text>
        </div>
        <div css={searchRowStyle}>
          <SearchField
            {...searchFieldProps}
            name="office-multi-select__search"
            placeholder="Search by name, ID, or address"
            autoComplete="off"
          />
        </div>
        <div css={listStyle}>
          {filteredOffices.length === 0 ? (
            <Text size="medium" color="light" css={emptyStateStyle}>
              No offices match your search.
            </Text>
          ) : (
            filteredOffices.map((office) => (
              <OfficeRow
                key={office.id}
                office={office}
                checked={selectedSet.has(office.id)}
                onToggle={toggleOffice}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

type OfficeRowProps = {
  office: OfficeOption;
  checked: boolean;
  onToggle: (id: string) => void;
};

const OfficeRow = ({ office, checked, onToggle }: OfficeRowProps) => {
  const fieldProps = useControlledField({
    type: 'checkbox',
    value: checked,
    onChange: () => onToggle(office.id),
  });

  return (
    <div css={rowStyle}>
      <CheckboxField
        {...fieldProps}
        name={`office-multi-select__office-${office.id}`}
        label={
          <div css={rowLabelStyle}>
            <Text as="span" weight="bold" size="medium">
              {office.name}
            </Text>
            <Text as="span" size="small" color="light">
              ID: <b>{office.id}</b>
              {'  |  '}
              Address: {office.address}
            </Text>
          </div>
        }
      />
    </div>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const labelStyle = (theme: WeaveTheme) => css`
  color: ${theme.colors.weaveBlue};
  margin-bottom: ${theme.spacing(1)};
`;

const cardStyle = (theme: WeaveTheme) => css`
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: ${theme.spacing(0.5)};
  background: ${theme.colors.white};
  overflow: hidden;
`;

const headerRowStyle = (theme: WeaveTheme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing(1.5, 2)};
  background: ${theme.colors.gray[100]};
  border-bottom: 1px solid ${theme.colors.gray[300]};
`;

const searchRowStyle = (theme: WeaveTheme) => css`
  padding: ${theme.spacing(1.5, 2)};
  border-bottom: 1px solid ${theme.colors.gray[300]};
`;

const listStyle = (theme: WeaveTheme) => css`
  max-height: 320px;
  overflow-y: auto;
  padding: ${theme.spacing(0.5, 0)};
`;

const rowStyle = (theme: WeaveTheme) => css`
  padding: ${theme.spacing(1, 2)};
  border-bottom: 1px solid ${theme.colors.gray[200]};

  &:last-of-type {
    border-bottom: none;
  }
`;

const rowLabelStyle = css`
  display: flex;
  flex-direction: column;
`;

const emptyStateStyle = (theme: WeaveTheme) => css`
  display: block;
  padding: ${theme.spacing(2)};
  text-align: center;
`;
